import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { getErrorFromResponse } from '@platform/helpers/response-error.helper';
import { cleanAuthTokenFromPath } from '@platform/helpers/url.helper';
import { AlertService } from '@platform/services/alert.service';
import { AuthenticationService } from '@platform/services/authentication.service';
import { updateDefaultAccount } from '@skudo/helpers/auth.helper';
import { BackToLoginService } from '@skudo/services/back-to-login.service';
import { UserService } from '@skudo/services/user.service';
import { User } from 'emma-common-ts/skudo';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
): Observable<boolean> | boolean => {
  const userService = inject(UserService);
  const authService = inject(AuthenticationService);
  const alertService = inject(AlertService);
  const backToLoginService = inject(BackToLoginService);
  const router = inject(Router);

  let res: Observable<unknown>;
  const authToken = route.queryParams['authToken'];
  const user = userService.getCurrentUser().getValue();

  if (authToken) {
    res = activateByToken(authToken, authService, userService);
  } else if (user) {
    res = activateByUser(user, userService);
  } else {
    res = throwError(() => 'Not allowed');
  }
  return res.pipe(
    map((prev) => {
      if ('authToken' in route.queryParams) {
        // Remove authToken from URL
        router
          .navigateByUrl(cleanAuthTokenFromPath(state.url), {
            replaceUrl: true,
          })
          .catch(console.error);
      }
      return Boolean(prev);
    }),
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        authService.logout();
        alertService.error(getErrorFromResponse(error));
      }
      // Something went wrong? Remove authToken from URL and return to login page
      backToLoginService.backToLogin({ from: cleanAuthTokenFromPath(state.url) }).catch(console.error);
      return of(false);
    })
  );
};

const activateByToken = (token: string, authService: AuthenticationService, userService: UserService) => {
  return authService.autoLogin(token).pipe(
    switchMap((res) => {
      if (res?.access_token) {
        userService.updateUser(res);
      }
      const user = userService.getCurrentUser().getValue();
      if (user) {
        return userService.verify(user);
      }
      return throwError(() => 'No user from token');
    })
  );
};

const activateByUser = (user: User, userService: UserService) => {
  return userService.verify(user).pipe(
    map((userScopeResponse) => {
      const { data } = userScopeResponse;
      if (data) {
        const currentKeys = userService.getEmmaKeys();
        // establecer default account
        if (currentKeys?.emmaAccountKey) {
          updateDefaultAccount(data, currentKeys.emmaAccountKey);
        }
        userService.updateUser({
          ...data.user,
          accounts: data.accounts,
        });
        return true;
      }
      return false;
    })
  );
};
